import {
  ChevronDown,
  LogOut,
  Settings as SettingsIcon,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { resolveErrorMessage } from "@/lib/roleForbidden";
import { useLogout } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

/**
 * Avatar dropdown — shared by the authenticated app shell `Header` and
 * the minimal `OnboardingHeader`.
 *
 * Surfaces sign-out from any authenticated route (including /onboarding,
 * which used to render a sidebar-less shell with no sign-out
 * affordance — production-readiness gap closed 2026-05-09).
 *
 * Click-outside closes the menu so a second click anywhere else dismisses
 * it cleanly. Escape-key dismissal mirrors the disclosure-button keyboard
 * spec and keeps the a11y floor honest.
 */

interface UserMenuProps {
  displayName: string | null;
  /** When true, the Settings menu item is hidden (e.g. on /onboarding
   *  where Settings would just bounce the user back through the app
   *  gate). */
  hideSettings?: boolean;
}

export default function UserMenu({
  displayName,
  hideSettings = false,
}: UserMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  function handleSignOut() {
    setOpen(false);
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate("/login", { replace: true });
      },
      onError: (error) => {
        toast.error(
          resolveErrorMessage(t, error) ||
            t("auth.userMenu.signOutFailedToast"),
        );
      },
    });
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "flex items-center gap-2 rounded-full border border-gray-200 px-2 py-1 text-sm font-medium text-gray-700",
          "hover:border-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("auth.userMenu.accountMenuAriaLabel")}
      >
        <span
          aria-hidden="true"
          className="flex size-6 items-center justify-center rounded-full bg-primary-500 text-xs font-semibold text-white"
        >
          {(displayName ?? "User").charAt(0).toUpperCase()}
        </span>
        <ChevronDown size={14} className="text-gray-500" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 w-48 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {!hideSettings && (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setOpen(false);
                navigate("/settings/account");
              }}
            >
              <SettingsIcon size={16} />
              {t("auth.userMenu.settings")}
            </button>
          )}
          {/* Team space — relocated out of the Sidebar rail into the avatar
            dropdown 2026-08-18 (ENG-SWARM-ASSETS-IA-POLISH item 7,
            supersedes 038ec7c95 "keep team space in sidebar utility
            rail"), between Settings and Sign out. Always visible — unlike
            Settings, /team does not require a selected app. */}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setOpen(false);
              navigate("/team");
            }}
          >
            <Users size={16} />
            {t("auth.userMenu.teamSpace")}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={logoutMutation.isPending}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            onClick={handleSignOut}
          >
            <LogOut size={16} />
            {logoutMutation.isPending
              ? t("auth.userMenu.signingOutEllipsis")
              : t("auth.userMenu.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}
